import { Link } from "react-router";
import type { Command } from "@shared-core/domain/object-ui/contract";
import type { PresentedStackBlock } from "@shared-core/domain/object-ui/record";
import { RefLinks } from "./RefLinks";
import { RowCommands } from "./RowCommands";
import { StateBadge } from "./StateBadge";

/**
 * A child's rows as stacked blocks (`children[].stack`), for rows that are mostly prose a person reads before acting:
 * the heading (its parts, its state and its commands) on one line, then each line in full, its label in a column on
 * the left and its points as a list with their notes beside them. A command the block offers but can't run says why
 * in a line of its own, first. A folded block (a decided action) keeps its heading, and any lines it keeps behind one
 * "Why" toggle. Nothing else is behind a hover or a disclosure.
 */
export function ChildStack({
  blocks,
  isPhone,
  declaredMainOf,
  onRun,
}: {
  blocks: readonly PresentedStackBlock[];
  isPhone: boolean;
  declaredMainOf: (commands: readonly Command[]) => string | null;
  /** Runs a block's command, its dialog headed by the row's command heading. */
  onRun: (command: Command, commandHeading: string) => void;
}) {
  return (
    <ol className="divide-y divide-border">
      {blocks.map((block) => {
        // A blocked command's reason is a line of its own, not a tooltip on a greyed-out button.
        const blocked = block.commands.filter((c) => !c.enabled && c.reason);
        return (
          <li
            key={block.key}
            className="min-w-0 space-y-1.5 py-3 first:pt-1 last:pb-1"
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 text-sm font-medium">
                {block.heading.map((part, n) => (
                  <span key={n} className="flex items-baseline gap-x-1.5">
                    {n > 0 && (
                      <span aria-hidden className="text-muted-foreground">
                        ·
                      </span>
                    )}
                    {part.refs ? (
                      <RefLinks refs={part.refs} />
                    ) : n === 0 && block.href ? (
                      <Link
                        to={block.href}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {part.text}
                      </Link>
                    ) : (
                      part.text
                    )}
                  </span>
                ))}
              </span>
              {block.state && (
                <StateBadge tone={block.stateTone}>{block.state}</StateBadge>
              )}
              {block.foldedDetail && (
                <span className="text-sm text-muted-foreground">
                  {block.foldedDetail}
                </span>
              )}
              {block.commands.length > 0 && (
                <span className="ml-auto">
                  <RowCommands
                    commands={block.commands}
                    declaredMain={declaredMainOf(block.commands)}
                    isPhone={isPhone}
                    onRun={(command) => onRun(command, block.commandHeading)}
                  />
                </span>
              )}
            </div>
            {(block.lines.length > 0 || blocked.length > 0) &&
              (block.isFolded ? (
                <details className="text-sm">
                  <summary className="cursor-pointer text-primary">Why</summary>
                  <div className="mt-2">{linesOf(block, blocked)}</div>
                </details>
              ) : (
                linesOf(block, blocked)
              ))}
          </li>
        );
      })}
    </ol>
  );
}

/** A block's lines: a blocked command's reason first, then each line's label on the left and its text and points. */
function linesOf(block: PresentedStackBlock, blocked: readonly Command[]) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]">
      {blocked.map((command) => (
        <div key={command.name} className="contents">
          <dt className="text-muted-foreground">Not available now</dt>
          <dd>
            {command.label.replace(/…$/, "")}: {command.reason}
          </dd>
        </div>
      ))}
      {block.lines.map((line, n) => (
        <div key={n} className="contents">
          <dt className="text-muted-foreground">{line.label}</dt>
          <dd className="space-y-1">
            {line.text && <p>{line.text}</p>}
            {/* Each point on its own row: a bullet, the point (wrapping under itself), and its source in a column
                        of its own on the right, so the sources line up and never wrap under the point. */}
            {line.items.length > 0 && (
              <ul className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:gap-x-4">
                {line.items.map((item, i) => (
                  <li key={i} className="contents">
                    <span aria-hidden className="text-muted-foreground">
                      •
                    </span>
                    <span>{item.text}</span>
                    <span className="col-start-2 text-muted-foreground sm:col-start-auto sm:whitespace-nowrap sm:text-right">
                      {item.note}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
